<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CaptainAssistantMediaAPI from 'dashboard/api/saas/captainAssistantMedia';
import CaptainVoicesAPI from 'dashboard/api/captain/voices';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const CAPABILITY_KEYS = [
  'quick_replies',
  'reactions',
  'media',
  'locations',
  'contacts',
  'stickers',
  'voice_replies',
];

const CAPABILITY_DEFAULTS = {
  quick_replies: true,
  reactions: true,
  media: false,
  locations: false,
  contacts: false,
  stickers: false,
  voice_replies: false,
};

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isSaving = computed(() => uiFlags.value.updatingItem);

const capabilities = ref({ ...CAPABILITY_DEFAULTS });
const voicesConfigured = ref(false);
const libraryVoices = ref([]);
const selectedVoiceId = ref('');
const selectedLibraryVoice = ref(null);
const isLoadingVoices = ref(false);
const playingPreview = ref(null);
let previewAudio = null;
const mediaFiles = ref([]);
const mediaEntries = ref({});
const locations = ref([]);
const contacts = ref([]);

const isLoadingMedia = ref(false);
const isUploading = ref(false);
const confirmDeleteId = ref(null);
const fileInputRef = ref(null);

const slugify = value =>
  value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');

const formatFileSize = bytes => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ensureMediaEntries = () => {
  mediaFiles.value.forEach(file => {
    if (!mediaEntries.value[file.blob_id]) {
      mediaEntries.value[file.blob_id] = { key: '', description: '' };
    }
  });
};

const hydrate = () => {
  const config = assistant.value?.config?.whatsapp_capabilities || {};
  CAPABILITY_KEYS.forEach(key => {
    capabilities.value[key] =
      config[key] !== undefined ? config[key] : CAPABILITY_DEFAULTS[key];
  });

  selectedVoiceId.value = config.voice_id || '';
  selectedLibraryVoice.value = null;

  mediaEntries.value = {};
  (config.media_library || []).forEach(entry => {
    mediaEntries.value[entry.blob_id] = {
      key: entry.key || '',
      description: entry.description || '',
    };
  });

  locations.value = (config.locations_list || []).map(location => ({
    key: location.key || '',
    name: location.name || '',
    address: location.address || '',
    latitude: location.latitude ?? '',
    longitude: location.longitude ?? '',
  }));

  contacts.value = (config.contacts_list || []).map(contact => ({
    key: contact.key || '',
    formattedName: contact.formatted_name || '',
    company: contact.company || '',
    description: contact.description || '',
    phone: contact.phones?.[0]?.phone || '',
    waId: contact.phones?.[0]?.wa_id || '',
  }));
};

const fetchMedia = async () => {
  isLoadingMedia.value = true;
  try {
    const { data } = await CaptainAssistantMediaAPI.get(assistantId.value);
    mediaFiles.value = data.payload || [];
    ensureMediaEntries();
  } catch (error) {
    useAlert(t('CAPTAIN.WHATSAPP.MEDIA.FETCH_ERROR'));
  } finally {
    isLoadingMedia.value = false;
  }
};

const fetchVoices = async () => {
  isLoadingVoices.value = true;
  try {
    const { data } = await CaptainVoicesAPI.get();
    voicesConfigured.value = !!data.configured;
    libraryVoices.value = data.library_voices || [];
  } catch (error) {
    voicesConfigured.value = false;
  } finally {
    isLoadingVoices.value = false;
  }
};

const stopPreview = () => {
  previewAudio?.pause();
  previewAudio = null;
  playingPreview.value = null;
};

const togglePreview = voice => {
  if (playingPreview.value === voice.voice_id) {
    stopPreview();
    return;
  }
  stopPreview();
  previewAudio = new Audio(voice.preview_url);
  previewAudio.onended = () => {
    playingPreview.value = null;
  };
  playingPreview.value = voice.voice_id;
  previewAudio.play();
};

const selectVoice = voice => {
  selectedVoiceId.value = voice.voice_id;
  selectedLibraryVoice.value =
    voice.public_owner_id && !voice.is_added_by_user ? voice : null;
};

const selectDefaultVoice = () => {
  selectedVoiceId.value = '';
  selectedLibraryVoice.value = null;
};

watch(
  assistantId,
  async id => {
    if (!id) return;
    try {
      await store.dispatch('captainAssistants/show', id);
    } catch (error) {
      // record may already be in the store; hydrate from whatever we have
    }
    hydrate();
    fetchMedia();
    fetchVoices();
  },
  { immediate: true }
);

const openFileDialog = () => fileInputRef.value?.click();

const handleFileChange = async event => {
  const file = event.target.files[0];
  event.target.value = '';
  if (!file) return;

  isUploading.value = true;
  try {
    const { data } = await CaptainAssistantMediaAPI.create(
      assistantId.value,
      file
    );
    mediaFiles.value.push(data);
    ensureMediaEntries();
    useAlert(t('CAPTAIN.WHATSAPP.MEDIA.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.WHATSAPP.MEDIA.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const handleDeleteMedia = async media => {
  try {
    await CaptainAssistantMediaAPI.delete(assistantId.value, media.id);
    mediaFiles.value = mediaFiles.value.filter(file => file.id !== media.id);
    delete mediaEntries.value[media.blob_id];
    useAlert(t('CAPTAIN.WHATSAPP.MEDIA.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.WHATSAPP.MEDIA.DELETE_ERROR'));
  } finally {
    confirmDeleteId.value = null;
  }
};

const addLocation = () => {
  locations.value.push({
    key: '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
  });
};

const removeLocation = index => locations.value.splice(index, 1);

const addContact = () => {
  contacts.value.push({
    key: '',
    formattedName: '',
    company: '',
    description: '',
    phone: '',
    waId: '',
  });
};

const removeContact = index => contacts.value.splice(index, 1);

const toNumberOrNull = value => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Spread the stored config first: capabilities managed by other surfaces
// (links, products, catalog_id, voice_name…) must survive a save from this tab.
const buildPayload = () => ({
  ...(assistant.value?.config?.whatsapp_capabilities || {}),
  ...capabilities.value,
  voice_id: selectedVoiceId.value || null,
  media_library: mediaFiles.value
    .map(file => ({
      key: mediaEntries.value[file.blob_id]?.key || '',
      blob_id: file.blob_id,
      description: mediaEntries.value[file.blob_id]?.description || '',
    }))
    .filter(entry => entry.key),
  locations_list: locations.value
    .filter(location => location.key)
    .map(location => ({
      key: location.key,
      name: location.name,
      address: location.address,
      latitude: toNumberOrNull(location.latitude),
      longitude: toNumberOrNull(location.longitude),
    })),
  contacts_list: contacts.value
    .filter(contact => contact.key)
    .map(contact => ({
      key: contact.key,
      formatted_name: contact.formattedName,
      description: contact.description,
      company: contact.company,
      phones: [{ phone: contact.phone, wa_id: contact.waId }],
    })),
});

const handleSave = async () => {
  try {
    // A community-library voice must be added to the ElevenLabs account
    // before it is usable for TTS; the returned id is the one to store.
    if (selectedLibraryVoice.value) {
      const { data } = await CaptainVoicesAPI.create({
        public_owner_id: selectedLibraryVoice.value.public_owner_id,
        voice_id: selectedLibraryVoice.value.voice_id,
        name: selectedLibraryVoice.value.name,
      });
      selectedVoiceId.value = data.voice_id;
      selectedLibraryVoice.value = null;
    }
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      config: {
        ...(assistant.value?.config || {}),
        whatsapp_capabilities: buildPayload(),
      },
    });
    useAlert(t('CAPTAIN.WHATSAPP.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.WHATSAPP.SAVE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.WHATSAPP.HEADER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-4">
        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('CAPTAIN.WHATSAPP.CAPABILITIES.TITLE')"
            :description="t('CAPTAIN.WHATSAPP.CAPABILITIES.DESCRIPTION')"
          />
          <div class="flex flex-col gap-4">
            <div
              v-for="key in CAPABILITY_KEYS"
              :key="key"
              class="flex items-center justify-between gap-4"
            >
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    t(
                      `CAPTAIN.WHATSAPP.CAPABILITIES.${key.toUpperCase()}.LABEL`
                    )
                  }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{
                    t(
                      `CAPTAIN.WHATSAPP.CAPABILITIES.${key.toUpperCase()}.DESCRIPTION`
                    )
                  }}
                </span>
              </div>
              <Switch v-model="capabilities[key]" />
            </div>
          </div>
        </div>

        <template v-if="capabilities.media">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.WHATSAPP.MEDIA.TITLE')"
              :description="t('CAPTAIN.WHATSAPP.MEDIA.DESCRIPTION')"
            />
            <div class="flex flex-col gap-4">
              <div>
                <input
                  ref="fileInputRef"
                  type="file"
                  accept=".pdf,image/*"
                  class="hidden"
                  @change="handleFileChange"
                />
                <Button
                  :label="t('CAPTAIN.WHATSAPP.MEDIA.UPLOAD')"
                  color="slate"
                  size="sm"
                  icon="i-lucide-upload"
                  :is-loading="isUploading"
                  :disabled="isUploading"
                  @click="openFileDialog"
                />
              </div>
              <p
                v-if="!mediaFiles.length && !isLoadingMedia"
                class="m-0 text-sm text-n-slate-11"
              >
                {{ t('CAPTAIN.WHATSAPP.MEDIA.EMPTY') }}
              </p>
              <div
                v-for="media in mediaFiles"
                :key="media.id"
                class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak"
              >
                <div class="flex items-center justify-between gap-4">
                  <div class="flex flex-col gap-1 min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ media.filename }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{ formatFileSize(media.byte_size) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <template v-if="confirmDeleteId === media.id">
                      <Button
                        :label="t('CAPTAIN.WHATSAPP.MEDIA.CONFIRM_DELETE')"
                        color="ruby"
                        size="sm"
                        @click="handleDeleteMedia(media)"
                      />
                      <Button
                        :label="t('CAPTAIN.WHATSAPP.MEDIA.CANCEL_DELETE')"
                        color="slate"
                        variant="faded"
                        size="sm"
                        @click="confirmDeleteId = null"
                      />
                    </template>
                    <Button
                      v-else
                      :label="t('CAPTAIN.WHATSAPP.MEDIA.DELETE')"
                      color="ruby"
                      variant="faded"
                      size="sm"
                      @click="confirmDeleteId = media.id"
                    />
                  </div>
                </div>
                <div
                  v-if="mediaEntries[media.blob_id]"
                  class="flex flex-col gap-3"
                >
                  <Input
                    :model-value="mediaEntries[media.blob_id].key"
                    :label="t('CAPTAIN.WHATSAPP.MEDIA.ENTRY.KEY.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.MEDIA.ENTRY.KEY.PLACEHOLDER')
                    "
                    @update:model-value="
                      value =>
                        (mediaEntries[media.blob_id].key = slugify(value))
                    "
                  />
                  <TextArea
                    v-model="mediaEntries[media.blob_id].description"
                    :label="t('CAPTAIN.WHATSAPP.MEDIA.ENTRY.DESCRIPTION.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.MEDIA.ENTRY.DESCRIPTION.PLACEHOLDER')
                    "
                    :max-length="500"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="capabilities.locations">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.WHATSAPP.LOCATIONS.TITLE')"
              :description="t('CAPTAIN.WHATSAPP.LOCATIONS.DESCRIPTION')"
            />
            <div class="flex flex-col gap-4">
              <p v-if="!locations.length" class="m-0 text-sm text-n-slate-11">
                {{ t('CAPTAIN.WHATSAPP.LOCATIONS.EMPTY') }}
              </p>
              <div
                v-for="(location, index) in locations"
                :key="index"
                class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak"
              >
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    :model-value="location.key"
                    :label="t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.KEY.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.KEY.PLACEHOLDER')
                    "
                    @update:model-value="
                      value => (location.key = slugify(value))
                    "
                  />
                  <Input
                    v-model="location.name"
                    :label="t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.NAME.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.NAME.PLACEHOLDER')
                    "
                  />
                </div>
                <Input
                  v-model="location.address"
                  :label="t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.ADDRESS.LABEL')"
                  :placeholder="
                    t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.ADDRESS.PLACEHOLDER')
                  "
                />
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    v-model="location.latitude"
                    type="number"
                    :label="
                      t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.LATITUDE.LABEL')
                    "
                    :placeholder="
                      t(
                        'CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.LATITUDE.PLACEHOLDER'
                      )
                    "
                  />
                  <Input
                    v-model="location.longitude"
                    type="number"
                    :label="
                      t('CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.LONGITUDE.LABEL')
                    "
                    :placeholder="
                      t(
                        'CAPTAIN.WHATSAPP.LOCATIONS.FIELDS.LONGITUDE.PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <div class="flex justify-end">
                  <Button
                    :label="t('CAPTAIN.WHATSAPP.LOCATIONS.REMOVE')"
                    color="ruby"
                    variant="faded"
                    size="sm"
                    @click="removeLocation(index)"
                  />
                </div>
              </div>
              <div>
                <Button
                  :label="t('CAPTAIN.WHATSAPP.LOCATIONS.ADD')"
                  color="slate"
                  size="sm"
                  icon="i-lucide-plus"
                  @click="addLocation"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="capabilities.contacts">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.WHATSAPP.CONTACTS.TITLE')"
              :description="t('CAPTAIN.WHATSAPP.CONTACTS.DESCRIPTION')"
            />
            <div class="flex flex-col gap-4">
              <p v-if="!contacts.length" class="m-0 text-sm text-n-slate-11">
                {{ t('CAPTAIN.WHATSAPP.CONTACTS.EMPTY') }}
              </p>
              <div
                v-for="(contact, index) in contacts"
                :key="index"
                class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak"
              >
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    :model-value="contact.key"
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.KEY.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.KEY.PLACEHOLDER')
                    "
                    @update:model-value="
                      value => (contact.key = slugify(value))
                    "
                  />
                  <Input
                    v-model="contact.formattedName"
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.NAME.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.NAME.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    v-model="contact.company"
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.COMPANY.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.COMPANY.PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="contact.description"
                    :label="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.DESCRIPTION.LABEL')
                    "
                    :placeholder="
                      t(
                        'CAPTAIN.WHATSAPP.CONTACTS.FIELDS.DESCRIPTION.PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    v-model="contact.phone"
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.PHONE.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.PHONE.PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="contact.waId"
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.WA_ID.LABEL')"
                    :placeholder="
                      t('CAPTAIN.WHATSAPP.CONTACTS.FIELDS.WA_ID.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="flex justify-end">
                  <Button
                    :label="t('CAPTAIN.WHATSAPP.CONTACTS.REMOVE')"
                    color="ruby"
                    variant="faded"
                    size="sm"
                    @click="removeContact(index)"
                  />
                </div>
              </div>
              <div>
                <Button
                  :label="t('CAPTAIN.WHATSAPP.CONTACTS.ADD')"
                  color="slate"
                  size="sm"
                  icon="i-lucide-plus"
                  @click="addContact"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="capabilities.stickers">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.WHATSAPP.STICKERS.TITLE')"
              :description="t('CAPTAIN.WHATSAPP.STICKERS.DESCRIPTION')"
            />
            <p
              class="m-0 p-4 text-sm rounded-lg text-n-slate-11 bg-n-alpha-black2"
            >
              {{ t('CAPTAIN.WHATSAPP.STICKERS.NOTE') }}
            </p>
          </div>
        </template>

        <template v-if="capabilities.voice_replies">
          <span class="h-px w-full bg-n-weak" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.WHATSAPP.VOICE.TITLE')"
              :description="t('CAPTAIN.WHATSAPP.VOICE.DESCRIPTION')"
            />
            <p
              v-if="!isLoadingVoices && !voicesConfigured"
              class="m-0 p-4 text-sm rounded-lg text-n-slate-11 bg-n-alpha-black2"
            >
              {{ t('CAPTAIN.WHATSAPP.VOICE.NOT_CONFIGURED') }}
            </p>
            <div v-else class="flex flex-col gap-2">
              <label
                class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
                :class="
                  selectedVoiceId === ''
                    ? 'border-n-brand bg-n-alpha-2'
                    : 'border-n-weak'
                "
              >
                <input
                  type="radio"
                  name="captain-voice"
                  :checked="selectedVoiceId === ''"
                  class="flex-shrink-0"
                  @change="selectDefaultVoice"
                />
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('CAPTAIN.WHATSAPP.VOICE.DEFAULT_OPTION') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAPTAIN.WHATSAPP.VOICE.DEFAULT_HINT') }}
                  </span>
                </div>
              </label>
              <label
                v-for="voice in libraryVoices"
                :key="voice.voice_id"
                class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
                :class="
                  selectedVoiceId === voice.voice_id
                    ? 'border-n-brand bg-n-alpha-2'
                    : 'border-n-weak'
                "
              >
                <input
                  type="radio"
                  name="captain-voice"
                  :checked="selectedVoiceId === voice.voice_id"
                  class="flex-shrink-0"
                  @change="selectVoice(voice)"
                />
                <Button
                  :icon="
                    playingPreview === voice.voice_id
                      ? 'i-lucide-pause'
                      : 'i-lucide-play'
                  "
                  slate
                  faded
                  xs
                  class="flex-shrink-0"
                  @click.prevent="togglePreview(voice)"
                />
                <div class="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ voice.name }}
                  </span>
                  <span class="text-xs text-n-slate-11 truncate">
                    {{
                      [
                        voice.labels.gender,
                        voice.labels.descriptive,
                        voice.labels.use_case,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    }}
                  </span>
                </div>
                <span class="text-xs text-n-slate-10 flex-shrink-0">
                  {{
                    t('CAPTAIN.WHATSAPP.VOICE.USED_BY', {
                      count: (voice.cloned_by_count || 0).toLocaleString(),
                    })
                  }}
                </span>
              </label>
            </div>
          </div>
        </template>

        <div
          class="sticky bottom-0 flex justify-end py-4 border-t bg-n-surface-1 border-n-weak"
        >
          <Button
            :label="t('CAPTAIN.WHATSAPP.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
